import { SortByEnum, TestParserFlags } from 'esm-test-parser';

/**
 * Built-in Mocha hook functions that can be defined in ESM modules.
 */
export const builtInFunctions = [
  'beforeAll',
  'beforeEach',
  'afterAll',
  'afterEach',
];

/**
 * Default options for the Mocha UI ESM.
 */
const defaultOptions: Partial<MochaUiEsmOptions> = {
  classContextPropertyName: 'suiteContext',
  parserFlags: {
    allowLiteralOnly: true,
    allowLiteralTitle: true,
    allowLiteralCases: true,
  },
  sort: SortByEnum.none,
};

/**
 * Configuration options for the Mocha UI for ESM.
 */
export class MochaUiEsmOptions {
  /**
   * Sets the property name used for accessing the context inside class tests.
   * The default value is 'suiteContext'.
   *
   * @example
   * ```ts
   * test1() {
   *   console.log(this.suiteContext.test.title)
   * }
   * ```
   */
  classContextPropertyName?: string = 'suiteContext';

  /**
   * Test case title template function.
   *
   * The default function will replace `$num` arguments with test case argument values.
   *
   * @example
   * - test case args: `[[5, 10]]`
   * - title input: `expected $1 to be $2 (case $i)`
   * - title output: `expected 5 to be 10 (case 1)`
   *
   * @param title - The template title string.
   * @param testCases - The test case arguments.
   * @param caseIndex - The 0-based index of the current test case.
   * @returns The formatted title.
   */
  testCaseTitleTemplate?: (
    title: string,
    testCases: any[],
    caseIndex: number,
  ) => string;

  /**
   * Flags passed to the underlying test parser.
   */
  parserFlags: Partial<TestParserFlags> = {
    allowLiteralOnly: true,
    allowLiteralTitle: true,
    allowLiteralCases: true,
  };

  /**
   * Specifies the sorting strategy for tests.
   *
   * Possible values:
   * - "none" (default)
   * - "byGroup"
   * - "byTitle"
   * - "byTitleAndGroup"
   */
  sort: SortByEnum = SortByEnum.none;

  /**
   * Initializes a new instance of the MochaUiEsmOptions class.
   *
   * @param options - Partial configuration to override defaults.
   */
  constructor(options?: Partial<MochaUiEsmOptions>) {
    Object.assign(this, defaultOptions, options);
  }
}
